import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DateRangeMenu } from '@/components/filter-bar';
import { Icon } from '@/components/icon';
import { MetricCard } from '@/components/metric-card';
import { ScraperConfigEditor } from '@/components/scraper-config-editor';
import { ErrorState, LoadingState } from '@/components/status-state';
import { getAnalyticsReport, getScraperConfigurations } from '@/lib/api';
import { rangeForPreset, type RangePreset } from '@/lib/date-range';
import type { SectionDefinition } from '@/lib/sections';
import type {
  AnalyticsBreakdownItem,
  AnalyticsMetric,
} from '@sia/analytics-contracts';

const ActivityChart = lazy(() => import('@/components/charts').then((module) => ({ default: module.ActivityChart })));
const AccountTrendChart = lazy(() => import('@/components/charts').then((module) => ({ default: module.AccountTrendChart })));
const BreakdownChart = lazy(() => import('@/components/charts').then((module) => ({ default: module.BreakdownChart })));
const ComparisonChart = lazy(() => import('@/components/charts').then((module) => ({ default: module.ComparisonChart })));
const TimeSeriesChart = lazy(() => import('@/components/charts').then((module) => ({ default: module.TimeSeriesChart })));

function metricById(metrics: AnalyticsMetric[], id: string) {
  return metrics.find((metric) => metric.id === id);
}

function BreakdownList({ items, formatAsPercent = false, formatAsShare = false }: { items: AnalyticsBreakdownItem[]; formatAsPercent?: boolean; formatAsShare?: boolean }) {
  if (items.length === 0) return <p className="awaiting">Awaiting data</p>;
  const maximum = Math.max(...items.map((item) => item.value), 1);
  const total = items.reduce((sum, item) => sum + item.value, 0);
  return <ol className="breakdown-list">
    {items.slice(0, 8).map((item) => <li key={item.key}>
      <span>{item.label}</span><span className="breakdown-list__bar"><i style={{ width: `${formatAsPercent ? Math.min(item.value, 100) : formatAsShare ? (total > 0 ? item.value * 100 / total : 0) : item.value * 100 / maximum}%` }} /></span>
      <strong>{formatAsPercent || formatAsShare ? `${(formatAsShare && total > 0 ? item.value * 100 / total : item.value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%` : item.value.toLocaleString()}</strong>
    </li>)}
  </ol>;
}

function Overview({ metrics, series, breakdowns }: {
  metrics: AnalyticsMetric[];
  series: Record<string, { date: string; value: number; secondaryValue?: number | null }[]>;
  breakdowns: Record<string, AnalyticsBreakdownItem[]>;
}) {
  const total = metricById(metrics, 'total-accounts');
  const engagementIds = ['push-engagement', 'email-open-rate'];
  const secondary = metrics.filter((metric) => metric.id !== 'total-accounts' && !engagementIds.includes(metric.id));
  const engagement = metrics.filter((metric) => engagementIds.includes(metric.id));
  return <>
    <section className="overview-hero panel">
      <div className="overview-total-panel">
        {total ? <MetricCard metric={total} featured /> : null}
        <Suspense fallback={<div className="chart-empty">Loading chart…</div>}>
          <AccountTrendChart data={series['account-trend'] ?? []} />
        </Suspense>
      </div>
      <div className="residency-panel">
        <div className="panel-title"><h2>Registered accounts by residency</h2><span>as of today</span></div>
        <BreakdownList items={breakdowns.residency ?? []} formatAsShare />
        <p>Percentage split among registered renters with a supplied residency.</p>
      </div>
    </section>
    <section className="metric-grid metric-grid--four">{secondary.map((metric) => <MetricCard metric={metric} key={metric.id} />)}</section>
    <p className="section-label">Demand & engagement · lifetime</p>
    <section className="overview-bottom">
      <article className="panel chart-panel"><div className="panel-title"><h2>Top cities searched</h2><span>lifetime</span></div><BreakdownList items={breakdowns['top-cities'] ?? []} formatAsPercent /></article>
      <article className="panel chart-panel"><div className="panel-title"><h2>Alert engagement</h2><span>lifetime, by channel</span></div>
        <div className="engagement-list">{engagement.map((metric) => <MetricCard metric={metric} key={metric.id} />)}</div>
      </article>
    </section>
    <p className="overview-footnote">Overview is fixed and always covers Day 0 → today. Date-reactive detail lives in the operational pages.</p>
  </>;
}

function BreakdownPanel({ title, data }: { title: string; data: AnalyticsBreakdownItem[] }) {
  return <article className="panel chart-panel">
    <div className="panel-title"><h2>{title}</h2><span>{data.length ? `${data.length} segments` : 'No observations'}</span></div>
    <Suspense fallback={<div className="chart-empty">Loading chart…</div>}>
      <BreakdownChart title={title} data={data} formatAsPercent={title === 'Top cities searched'} />
    </Suspense>
  </article>;
}

function BreakdownSplitCard({ title, data }: { title: string; data: AnalyticsBreakdownItem[] }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const shares = data.map((item) => ({ ...item, share: total > 0 ? item.value * 100 / total : 0 }));
  return <article className="panel active-residency-card breakdown-split-card">
    <h2>{title}</h2>
    {total > 0 ? <>
      <div className="active-residency-card__bar" aria-label={shares.map((item) => `${item.label} ${item.share.toFixed(1)}%`).join(', ')}>
        {shares.map((item, index) => <i className={index % 2 === 0 ? 'active-residency-card__bar-israel' : 'active-residency-card__bar-abroad'} style={{ width: `${item.share}%` }} key={item.key} />)}
      </div>
      <div className="active-residency-card__legend">
        {shares.map((item, index) => <span key={item.key}><i className={`active-residency-card__swatch ${index % 2 === 0 ? 'active-residency-card__swatch--israel' : 'active-residency-card__swatch--abroad'}`} />{item.label} · {item.share.toFixed(0)}%</span>)}
      </div>
    </> : <p className="awaiting">Awaiting data</p>}
  </article>;
}

function RuntimeStatCards({ average, maximum }: { average: AnalyticsBreakdownItem[]; maximum: AnalyticsBreakdownItem[] }) {
  const createMetric = (source: 'yad2' | 'facebook', kind: 'Average' | 'Max.', items: AnalyticsBreakdownItem[]): AnalyticsMetric => {
    const item = items.find((candidate) => candidate.key === source);
    const label = `${kind} ${source === 'yad2' ? 'Yad2' : 'Facebook'} runtime`;
    if (!item) return { id: `${kind.toLowerCase()}-${source}-runtime`, label, unit: 'minutes', state: 'awaiting_data', value: null, formattedValue: null };
    const previousValue = item.secondaryValue;
    return {
      id: `${kind.toLowerCase()}-${source}-runtime`, label, unit: 'minutes', state: 'ready', value: item.value,
      formattedValue: `${item.value.toLocaleString(undefined, { maximumFractionDigits: 1 })} min`, previousValue,
      deltaPercent: previousValue === undefined || previousValue === null
        ? null
        : previousValue === 0
          ? item.value === 0 ? 0 : null
          : (item.value - previousValue) * 100 / previousValue,
    };
  };
  return <section className="metric-grid metric-grid--four scraper-runtime-grid">
    {[
      createMetric('yad2', 'Average', average),
      createMetric('facebook', 'Average', average),
      createMetric('yad2', 'Max.', maximum),
      createMetric('facebook', 'Max.', maximum),
    ].map((metric) => <MetricCard metric={metric} neutralTrend key={metric.id} />)}
  </section>;
}

function DemandConcentrationPanel({ cities, neighborhoods }: {
  cities: AnalyticsBreakdownItem[];
  neighborhoods: AnalyticsBreakdownItem[];
}) {
  const formatShare = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  const cityCards = cities.slice(0, 6).map((city) => ({
    city,
    neighborhoods: neighborhoods
      .filter((item) => item.label.startsWith(`${city.label} · `))
      .slice(0, 3),
  }));

  return <section className="demand-concentration-section">
    <p className="section-label demand-concentration-section__eyebrow">Where demand concentrates</p>
    <article className="panel demand-concentration-panel">
      <div className="demand-concentration-panel__header">
        <h2>Top cities searched</h2>
        <span>with top neighborhoods per city</span>
      </div>
      {cityCards.length > 0 ? <div className="demand-city-grid">
        {cityCards.map(({ city, neighborhoods: cityNeighborhoods }) => <article className="demand-city-card" key={city.key}>
          <div className="demand-city-card__header">
            <h3>{city.label}</h3>
            <strong>{formatShare(city.value)}</strong>
          </div>
          {cityNeighborhoods.length > 0 ? <div className="demand-city-card__neighborhoods">
            {cityNeighborhoods.map((item) => <div className="demand-city-card__neighborhood" key={item.key}>
              <span>{item.label.slice(city.label.length + 3)}</span>
              <i><b style={{ width: `${Math.min(Math.max(item.value, 0), 100)}%` }} /></i>
              <strong>{formatShare(item.value)}</strong>
            </div>)}
          </div> : <p className="demand-city-card__empty">No neighborhood data</p>}
        </article>)}
      </div> : <p className="awaiting">Awaiting data</p>}
      <p className="demand-concentration-panel__footnote">Neighborhood % is share of searches within that city, not of total platform-wide searches.</p>
    </article>
  </section>;
}

function BudgetDistributionPanel({ data, median }: { data: AnalyticsBreakdownItem[]; median?: AnalyticsMetric }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const formatShare = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  return <article className="panel budget-distribution-panel">
    <div className="budget-distribution-panel__header">
      <h2>Budget distribution</h2>
      <span>monthly, ₪</span>
    </div>
    {data.length > 0 && total > 0 ? <div className="budget-distribution-list">
      {data.map((item) => {
        const share = item.value * 100 / total;
        return <div className="budget-distribution-row" key={item.key}>
          <span>{item.label.replace('Under ₪4k', '< ₪4k').replace('₪15k+', '₪15k+')}</span>
          <i><b style={{ width: `${share}%` }} /></i>
          <strong>{formatShare(share)}</strong>
        </div>;
      })}
    </div> : <p className="awaiting">Awaiting data</p>}
    {median?.state === 'ready' ? <p className="budget-distribution-panel__note">Median {median.formattedValue} — cross-references inventory pricing in Inventory tab.</p> : null}
  </article>;
}

function PreferenceDemandPanel({ features, rooms }: { features: AnalyticsBreakdownItem[]; rooms: AnalyticsBreakdownItem[] }) {
  const formatShare = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  const featureRows = [...features].sort((left, right) => right.value - left.value).slice(0, 5);
  const roomBuckets = [
    ...Array.from({ length: 9 }, (_, index) => ({ key: `${index + 1}`, label: `${index + 1} ${index === 0 ? 'room' : 'rooms'}` })),
    { key: '10-plus', label: '10+ rooms' },
  ];
  const roomDemandByBucket = rooms.reduce((demand, item) => {
    const numericKey = Number.parseFloat(item.key);
    const bucketKey = item.key === '10-plus'
      ? '10-plus'
      : Number.isFinite(numericKey) && Number.isInteger(numericKey) && numericKey >= 1 && numericKey <= 9
        ? `${numericKey}`
        : Number.isFinite(numericKey) && numericKey >= 10
          ? '10-plus'
          : null;
    if (bucketKey) demand.set(bucketKey, (demand.get(bucketKey) ?? 0) + item.value);
    return demand;
  }, new Map<string, number>());
  const roomRows = roomBuckets.map((bucket) => ({
    ...bucket,
    value: roomDemandByBucket.get(bucket.key) ?? 0,
  }));
  const roomTotal = roomRows.reduce((sum, item) => sum + item.value, 0);
  const roomDisplayRows = roomTotal > 0
    ? roomRows.map((item, index) => {
      if (index === roomRows.length - 1) {
        const roundedPrevious = roomRows.slice(0, -1)
          .reduce((sum, previous) => sum + Math.round((previous.value * 100 / roomTotal) * 10) / 10, 0);
        return { ...item, value: Math.max(0, 100 - roundedPrevious) };
      }
      return { ...item, value: Math.round((item.value * 100 / roomTotal) * 10) / 10 };
    })
    : roomRows;
  const rows = (items: AnalyticsBreakdownItem[]) => <div className="preference-demand-list">
    {items.map((item) => <div className="preference-demand-row" key={item.key}>
      <span>{item.label}</span>
      <i><b style={{ width: `${Math.min(Math.max(item.value, 0), 100)}%` }} /></i>
      <strong>{formatShare(item.value)}</strong>
    </div>)}
  </div>;
  return <section className="preference-demand-section">
    <p className="section-label preference-demand-section__eyebrow">What people want</p>
    <div className="preference-demand-grid">
      <article className="panel preference-demand-panel">
        <div className="preference-demand-panel__header"><h2>Feature demand</h2><span>% of profiles requiring</span></div>
        {featureRows.length > 0 ? rows(featureRows) : <p className="awaiting">Awaiting data</p>}
      </article>
      <article className="panel preference-demand-panel">
        <div className="preference-demand-panel__header"><h2>Room-count demand</h2><span>% of tenant demand</span></div>
        {rooms.length > 0 ? rows(roomDisplayRows) : <p className="awaiting">Awaiting data</p>}
      </article>
    </div>
  </section>;
}

function ListingArrivalTimePanel({ data }: { data: AnalyticsBreakdownItem[] }) {
  const windows = [
    [6, '6–9am'], [9, '9am–12pm'], [12, '12–3pm'],
    [15, '3–6pm'], [18, '6–9pm'], [21, '9pm–12am'],
  ] as const;
  const values = windows.map(([start, label]) => ({
    key: String(start),
    label,
    value: data.filter((item) => {
      const hour = Number.parseInt(item.key, 10);
      return hour >= start && hour < start + 3;
    }).reduce((sum, item) => sum + item.value, 0),
  }));
  const total = values.reduce((sum, item) => sum + item.value, 0);
  const formatShare = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  return <article className="panel listing-arrival-time-panel">
    <div className="listing-arrival-time-panel__header"><h2>New listing arrival time</h2><span>by hour of day</span></div>
    {total > 0 ? <div className="listing-arrival-time-list">
      {values.map((item) => {
        const share = item.value * 100 / total;
        return <div className="listing-arrival-time-row" key={item.key}>
          <span>{item.label}</span><i><b style={{ width: `${share}%` }} /></i><strong>{formatShare(share)}</strong>
        </div>;
      })}
    </div> : <p className="awaiting">Awaiting data</p>}
    {/* <p className="listing-arrival-time-panel__note">Peaks around evening hours — informs scraper run cadence and alert-capacity planning.</p> */}
  </article>;
}

function SourceSplitPanel({ data, brokerPrivate, cityBrokerPrivate }: {
  data: AnalyticsBreakdownItem[];
  brokerPrivate: AnalyticsBreakdownItem[];
  cityBrokerPrivate: AnalyticsBreakdownItem[];
}) {
  const yad2 = data.find((item) => item.key === 'yad2')?.value ?? 0;
  const facebook = data.find((item) => item.key === 'facebook')?.value ?? 0;
  const yad2Count = data.find((item) => item.key === 'yad2')?.count ?? yad2;
  const facebookCount = data.find((item) => item.key === 'facebook')?.count ?? facebook;
  const total = yad2 + facebook;
  const yad2Share = total > 0 ? yad2 * 100 / total : 0;
  const facebookShare = total > 0 ? facebook * 100 / total : 0;
  const broker = brokerPrivate.find((item) => item.key === 'broker')?.value ?? 0;
  const privateListings = brokerPrivate.find((item) => item.key === 'private')?.value ?? 0;
  const brokerCount = brokerPrivate.find((item) => item.key === 'broker')?.count;
  const privateCount = brokerPrivate.find((item) => item.key === 'private')?.count;
  const brokerTotal = broker + privateListings;
  const formatListingCount = (count: number | null | undefined) => count == null ? '' : `${count.toLocaleString()} listing${count === 1 ? '' : 's'}`;
  const listingCountSuffix = (count: number | null | undefined) => count == null ? '' : ` (${formatListingCount(count)})`;
  return <article className="panel active-residency-card">
    <div className="source-split-panel__section">
      <h2>By source</h2>
    {total > 0 ? <>
      <div className="active-residency-card__bar" aria-label={`Yad2 ${yad2Share.toFixed(0)}%${listingCountSuffix(yad2Count)}, Facebook ${facebookShare.toFixed(0)}%${listingCountSuffix(facebookCount)}`}>
        <i className="active-residency-card__bar-israel" style={{ width: `${yad2Share}%` }} />
        <i className="active-residency-card__bar-abroad source-split-panel__bar-facebook" style={{ width: `${facebookShare}%` }} />
      </div>
      <div className="active-residency-card__legend">
          <span><i className="active-residency-card__swatch active-residency-card__swatch--israel" />Yad2 {yad2Share.toFixed(0)}%{listingCountSuffix(yad2Count)}</span>
          <span><i className="active-residency-card__swatch active-residency-card__swatch--abroad" />Facebook {facebookShare.toFixed(0)}%{listingCountSuffix(facebookCount)}</span>
      </div>
    </> : <p className="awaiting">Awaiting data</p>}
    </div>
    <div className="source-split-panel__section">
      <h2>Broker vs private</h2>
      {brokerTotal > 0 ? <>
        <div className="active-residency-card__bar" aria-label={`Broker ${broker.toFixed(0)}%${listingCountSuffix(brokerCount)}, Private ${privateListings.toFixed(0)}%${listingCountSuffix(privateCount)}`}>
          <i className="active-residency-card__bar-israel" style={{ width: `${broker}%` }} />
          <i className="active-residency-card__bar-abroad" style={{ width: `${privateListings}%` }} />
        </div>
        <div className="active-residency-card__legend">
          <span><i className="active-residency-card__swatch active-residency-card__swatch--israel" />Broker · {broker.toFixed(0)}%{listingCountSuffix(brokerCount)}</span>
          <span><i className="active-residency-card__swatch active-residency-card__swatch--abroad" />Private · {privateListings.toFixed(0)}%{listingCountSuffix(privateCount)}</span>
        </div>
        {/* <p className="source-split-panel__note">Brokered listings have a customer.agencyName; listings without one are private.</p> */}
      </> : <p className="awaiting">Awaiting data</p>}
      {cityBrokerPrivate.length > 0 ? <div className="source-split-panel__city-breakdown">
        <h3>Top 5 searched cities</h3>
        <div className="source-split-panel__city-list">
          {cityBrokerPrivate.map((city) => {
            const cityBroker = city.value;
            const cityPrivate = city.secondaryValue ?? 0;
            const cityBrokerCount = city.count;
            const cityPrivateCount = city.secondaryCount;
            return <div className="source-split-panel__city-row" key={city.key}>
              <strong>{city.label}</strong>
              <div className="active-residency-card__bar" aria-label={`${city.label}: Broker ${cityBroker.toFixed(0)}%${listingCountSuffix(cityBrokerCount)}, Private ${cityPrivate.toFixed(0)}%${listingCountSuffix(cityPrivateCount)}`}>
                <i className="active-residency-card__bar-israel" style={{ width: `${cityBroker}%` }} />
                <i className="active-residency-card__bar-abroad" style={{ width: `${cityPrivate}%` }} />
              </div>
              <div className="source-split-panel__city-legend">
                <span><i className="active-residency-card__swatch active-residency-card__swatch--israel" />Broker · {cityBroker.toFixed(0)}%{listingCountSuffix(cityBrokerCount)}</span>
                <span><i className="active-residency-card__swatch active-residency-card__swatch--abroad" />Private · {cityPrivate.toFixed(0)}%{listingCountSuffix(cityPrivateCount)}</span>
              </div>
            </div>;
          })}
        </div>
      </div> : null}
    </div>
  </article>;
}

function FacebookCategoryPanel({ categories, groups, selectedGroupId, onGroupChange, from, toExclusive }: {
  categories: AnalyticsBreakdownItem[];
  groups: AnalyticsBreakdownItem[];
  selectedGroupId: number | null;
  onGroupChange: (groupId: number | null) => void;
  from: string;
  toExclusive: string;
}) {
  const activeGroups = groups.filter((group) => group.value > 0);
  const selectedGroup = activeGroups.find((group) => group.key === String(selectedGroupId));
  const categoryReport = useQuery({
    queryKey: ['facebook-category-breakdown', from, toExclusive, selectedGroupId],
    queryFn: ({ signal }) => getAnalyticsReport({
      section: 'listing-inventory', from, toExclusive, groupId: selectedGroupId, refresh: false, signal,
    }),
    enabled: selectedGroupId !== null,
  });
  const displayedCategories = selectedGroupId === null
    ? categories
    : categoryReport.data?.data.breakdowns['facebook-categories'] ?? [];
  const total = displayedCategories.reduce((sum, category) => sum + (category.secondaryValue ?? 0), 0);
  const rentableShare = displayedCategories
    .filter((category) => ['rent_long_term', 'rent_short_term', 'sublet'].includes(category.key))
    .reduce((sum, category) => sum + category.value, 0);
  const formatPercent = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

  return <section className="facebook-category-section">
    <p className="section-label facebook-category-section__eyebrow">FB post category distribution per group</p>
    <article className="panel facebook-category-panel">
      <div className="facebook-category-panel__toolbar">
        <h2>{selectedGroup ? `“${selectedGroup.label}”` : 'All Facebook groups'}</h2>
        <label>
          <span className="sr-only">Facebook group</span>
          <select value={selectedGroupId ?? ''} onChange={(event) => onGroupChange(event.target.value ? Number(event.target.value) : null)}>
            <option value="">All Facebook groups</option>
            {activeGroups.map((group) => <option value={group.key} key={group.key}>{group.label}</option>)}
          </select>
        </label>
      </div>
      <div className="facebook-category-panel__heading"><span>% of posts scraped</span></div>
      {categoryReport.isFetching ? <p className="awaiting">Loading group data…</p> : displayedCategories.length > 0 && total > 0 ? <div className="facebook-category-list">
        {displayedCategories.slice(0, 7).map((category) => <div className="facebook-category-row" key={category.key}>
          <span>{category.key}</span>
          <i><b style={{ width: `${Math.min(Math.max(category.value, 0), 100)}%` }} /></i>
          <strong>{formatPercent(category.value)}</strong>
        </div>)}
      </div> : <p className="awaiting">Awaiting data</p>}
      {displayedCategories.length > 0 && total > 0 ? <p className="facebook-category-panel__note">Rentable share (long+short+sublet) = {formatPercent(rentableShare)} — a strong source. Groups dominated by tenant_searching/other are candidates to drop.</p> : null}
    </article>
  </section>;
}

function ActiveResidencyCards({ data }: { data: AnalyticsBreakdownItem[] }) {
  const windows = ['DAU', 'WAU', 'MAU'];
  return <section className="active-residency-section">
    <p className="section-label active-residency-section__eyebrow">Active users by residency</p>
    <div className="active-residency-grid">
      {windows.map((window) => {
        const rows = data.filter((item) => item.label.startsWith(`${window} ·`));
        const inIsrael = rows.find((item) => item.label.endsWith('In Israel'))?.value ?? 0;
        const abroad = rows.find((item) => item.label.endsWith('Abroad'))?.value ?? 0;
        const total = inIsrael + abroad;
        const israelShare = total > 0 ? inIsrael * 100 / total : 0;
        const abroadShare = total > 0 ? abroad * 100 / total : 0;
        return <article className="panel active-residency-card" key={window}>
          <h2>{window}</h2>
          <div className="active-residency-card__bar" aria-label={`${window}: In Israel ${israelShare.toFixed(1)}%, Abroad ${abroadShare.toFixed(1)}%`}>
            <i className="active-residency-card__bar-israel" style={{ width: `${israelShare}%` }} />
            <i className="active-residency-card__bar-abroad" style={{ width: `${abroadShare}%` }} />
          </div>
          <div className="active-residency-card__legend">
            <span><i className="active-residency-card__swatch active-residency-card__swatch--israel" />IL {israelShare.toFixed(0)}%</span>
            <span><i className="active-residency-card__swatch active-residency-card__swatch--abroad" />Abroad {abroadShare.toFixed(0)}%</span>
          </div>
        </article>;
      })}
    </div>
  </section>;
}

function MetricPairCard({ title, average, median, data, unit }: {
  title: string;
  average: AnalyticsMetric;
  median: AnalyticsMetric;
  data: { date: string; value: number; secondaryValue?: number | null }[];
  unit: 'count' | 'percent' | 'days' | 'per-day';
}) {
  const formatValue = (value: number) => unit === 'per-day'
    ? `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} /day`
    : unit === 'percent'
      ? `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
      : unit === 'days'
        ? `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} days`
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const displayValue = (metric: AnalyticsMetric, value?: number) => metric.state === 'ready'
    ? formatValue(value ?? metric.value)
    : metric.state === 'not_applicable' ? 'Not applicable' : 'Awaiting data';
  const dailyAverages = data.map((point) => point.value);
  const dailyMedians = data
    .map((point) => point.secondaryValue)
    .filter((value): value is number => value !== null && value !== undefined);
  const medianValue = (values: number[]) => {
    const sorted = [...values].sort((left, right) => left - right);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[middle - 1] + sorted[middle]) / 2
      : sorted[middle];
  };
  const averageOfDailyAverages = dailyAverages.length > 0
    ? dailyAverages.reduce((total, value) => total + value, 0) / dailyAverages.length
    : undefined;
  const medianOfDailyMedians = dailyMedians.length > 0
    ? medianValue(dailyMedians)
    : undefined;
  return <article className="metric-pair-card panel">
    <h2>{title}</h2>
    <div className="metric-pair-card__values">
      <div><span>Average</span><strong>{displayValue(average, averageOfDailyAverages)}</strong></div>
      <div><span>Median</span><strong>{displayValue(median, medianOfDailyMedians)}</strong></div>
    </div>
    <Suspense fallback={<div className="chart-empty">Loading chart…</div>}>
      <ComparisonChart title={title} data={data} unit={unit} />
    </Suspense>
    <p className="metric-pair-card__note">Denominator = users active in the selected range (not all registered users). A wide avg–median gap flags a few power users skewing the mean.</p>
  </article>;
}

const breakdownTitles: Record<string, string> = {
  'active-residency': 'Active users by residency', budget: 'Maximum budget distribution', cities: 'Top cities searched',
  neighborhoods: 'Top neighbourhoods searched', features: 'Feature demand', rooms: 'Room-count demand',
  'live-by-source': 'Live listings by source', 'facebook-categories': 'Facebook post categories',
  'arrival-hours': 'Listing arrival time', 'broker-private': 'Broker vs private',
  'arrival-weekdays': 'Listing arrivals by weekday',
  'delivery-mix': 'Delivery channel mix', 'profile-overrides': 'Per-profile overrides',
  sources: 'Runs by source', 'facebook-groups': 'Facebook-sourced listings by group',
  'source-average-durations': 'Average runtime by source',
  'source-maximum-durations': 'Max. runtime by source',
  'facebook-group-average-durations': 'Average Facebook group duration',
  'facebook-group-maximum-durations': 'Maximum Facebook group duration',
};

const seriesTitles: Record<string, string> = {
  'new-listings': 'New listings scraped per day',
};

function StandardDashboard({ averagePerUser = false, globalUsage = false, preferenceProfiles = false, inventory = false, alertsEngagement = false, scraperHealth = false, scraperConfigs = [], scraperConfigError, groupId = null, onGroupChange, from, toExclusive, metrics, series, breakdowns }: {
  metrics: AnalyticsMetric[];
  series: Record<string, { date: string; value: number; secondaryValue?: number | null }[]>;
  breakdowns: Record<string, AnalyticsBreakdownItem[]>;
  globalUsage?: boolean;
  averagePerUser?: boolean;
  preferenceProfiles?: boolean;
  inventory?: boolean;
  alertsEngagement?: boolean;
  scraperHealth?: boolean;
  scraperConfigs?: import('@sia/analytics-contracts').AnalyticsScraperConfig[];
  scraperConfigError?: string;
  groupId?: number | null;
  onGroupChange?: (groupId: number | null) => void;
  from: string;
  toExclusive: string;
}) {
  const firstSix = globalUsage ? metrics.slice(0, 6) : [];
  const globalUsageTrendKeys: Record<string, string> = {
    signups: 'signups', deletions: 'deletions', 'average-dau': 'average-dau',
    'average-wau': 'average-wau', 'average-mau': 'average-mau', stickiness: 'stickiness',
  };
  const remainingMetrics = globalUsage ? metrics.slice(6) : metrics;
  const inventoryMetricOrder = ['new-listings-per-day', 'active-facebook-groups', 'missing-geography'];
  const inventoryMetricLabels: Record<string, string> = {
    'live-listings': 'Total listings',
    'new-listings-per-day': 'New listings',
    'active-facebook-groups': 'FB groups tracked',
    'missing-geography': 'Missing city/nbhd/street',
  };
  const inventoryMetrics = inventoryMetricOrder
    .map((id) => metrics.find((metric) => metric.id === id))
    .filter((metric): metric is AnalyticsMetric => metric !== undefined);
  const preferenceMetricOrder = ['profiles-created', 'zero-match-rate', 'median-budget-max', 'average-budget-max'];
  const preferenceMetricLabels: Record<string, string> = {
    'profiles-created': 'Profiles created',
    'zero-match-rate': 'Zero-match profiles',
    'median-budget-max': 'Max. budget (median)',
    'average-budget-max': 'Max. budget (average)',
  };
  const preferenceHiddenNotes = new Set(['zero-match-rate', 'average-budget-max']);
  const preferenceMetrics = preferenceMetricOrder
    .map((id) => metrics.find((metric) => metric.id === id))
    .filter((metric): metric is AnalyticsMetric => metric !== undefined);
  const retentionOrder = ['d1-retention', 'd7-retention', 'd30-retention', 'signup-profile-conversion'];
  const retentionMetrics = retentionOrder
    .map((id) => remainingMetrics.find((metric) => metric.id === id))
    .filter((metric): metric is AnalyticsMetric => metric !== undefined);
  const pairedMetricIds = new Set([
    'average-sessions', 'median-sessions', 'average-matches', 'median-matches',
    'average-save-rate', 'median-save-rate', 'average-tap-rate', 'median-tap-rate',
    'average-inactivity', 'median-inactivity',
  ]);
  const alertMetricIds = new Set([
    'total-sent', 'push-sent', 'push-engagement-rate', 'listing-open-rate', 'email-sent', 'email-open-rate', 'email-click-rate',
    'instant-opt-out', 'digest-opt-out',
  ]);
  const scraperMetricIds = new Set(['failed-runs', 'average-duration', 'maximum-duration', 'failed-group-runs']);
  const otherMetrics = globalUsage
    ? remainingMetrics.filter((metric) => !retentionOrder.includes(metric.id))
    : averagePerUser
      ? metrics.filter((metric) => metric.id !== 'active-users' && !pairedMetricIds.has(metric.id))
      : preferenceProfiles
        ? remainingMetrics.filter((metric) => metric.id !== 'furnished-demand')
        : inventory
          ? metrics.filter((metric) => metric.id !== 'live-listings' && !inventoryMetricOrder.includes(metric.id))
      : remainingMetrics.filter((metric) => !alertsEngagement && !scraperHealth || (!alertMetricIds.has(metric.id) && !scraperMetricIds.has(metric.id)));
  const alertMetrics = (ids: string[]) => ids
    .map((id) => metrics.find((metric) => metric.id === id))
    .filter((metric): metric is AnalyticsMetric => metric !== undefined);
  const scraperFailureMetric = (source: AnalyticsBreakdownItem): AnalyticsMetric => ({
    id: `failed-${source.key}-runs`, label: `Failed ${source.label} runs`, state: 'ready',
    value: source.value, formattedValue: source.value.toLocaleString(), unit: 'count',
    previousValue: source.secondaryValue,
    deltaPercent: source.secondaryValue === undefined || source.secondaryValue === null
      ? null
      : source.secondaryValue === 0
        ? source.value === 0 ? 0 : null
        : (source.value - source.secondaryValue) * 100 / source.secondaryValue,
  });
  const scraperFailures = (breakdowns.sources ?? []).filter((item) => item.key === 'yad2' || item.key === 'facebook').map(scraperFailureMetric);
  const scraperRunMetric = (source: AnalyticsBreakdownItem, label: string): AnalyticsMetric => ({
    id: `total-${source.key}-runs`, label, state: 'ready', value: source.value,
    formattedValue: source.value.toLocaleString(), unit: 'count', previousValue: source.secondaryValue,
    deltaPercent: source.secondaryValue === undefined || source.secondaryValue === null
      ? null
      : source.secondaryValue === 0
        ? source.value === 0 ? 0 : null
        : (source.value - source.secondaryValue) * 100 / source.secondaryValue,
  });
  const scraperRunTotals = (breakdowns['run-totals'] ?? [])
    .filter((item) => item.key === 'yad2' || item.key === 'facebook');
  const totalRuns = scraperRunTotals.reduce((total, item) => total + item.value, 0);
  const previousTotalRuns = scraperRunTotals.reduce((total, item) => total + (item.secondaryValue ?? 0), 0);
  const totalRunsMetric: AnalyticsMetric = scraperRunMetric({
    key: 'all', label: 'All', value: totalRuns, secondaryValue: previousTotalRuns,
  }, 'Total runs');
  const retentionLabels: Record<string, string> = {
    'd1-retention': 'D1', 'd7-retention': 'D7', 'd30-retention': 'D30', 'signup-profile-conversion': 'Signup → profile',
  };
  const retentionNotes: Record<string, string> = {
    'd1-retention': 'Matured cohorts in range',
    'd7-retention': 'Excludes cohorts <7d old',
    'd30-retention': 'Excludes cohorts <30d old',
    'signup-profile-conversion': 'Cohort = signups in range',
  };
  const pairDefinitions = [
    ['Sessions per active user', 'average-sessions', 'median-sessions', 'sessions', 'per-day'],
    ['Matches received per active user', 'average-matches', 'median-matches', 'matches', 'count'],
    ['Per-user save rate', 'average-save-rate', 'median-save-rate', 'save-rate', 'percent'],
    ['Per-user outbound tap rate', 'average-tap-rate', 'median-tap-rate', 'tap-rate', 'percent'],
    ['Days since last activity', 'average-inactivity', 'median-inactivity', 'inactivity', 'days'],
  ] as const;
  return <>
    {globalUsage ? <>
      <section className="global-usage-grid global-usage-grid--headline">
        {firstSix.slice(0, 2).map((metric) => <MetricCard metric={metric} trend={series[globalUsageTrendKeys[metric.id]]} trendUnit={metric.id === 'stickiness' ? 'percent' : 'count'} key={metric.id} />)}
      </section>
      <section className="global-usage-grid global-usage-grid--secondary">
        {firstSix.slice(2).map((metric) => <MetricCard metric={metric} trend={series[globalUsageTrendKeys[metric.id]]} trendUnit={metric.id === 'stickiness' ? 'percent' : 'count'} key={metric.id} />)}
      </section>
    </> : null}
    {inventory ? <section className="metric-grid metric-grid--four">
      {inventoryMetrics.map((metric) => <MetricCard metric={metric} label={inventoryMetricLabels[metric.id]} note={metric.id === 'missing-geography' ? '' : undefined} invertTrend={metric.id === 'missing-geography'} trend={metric.id === 'new-listings-per-day' ? series['new-listings'] : undefined} trendTitle={metric.id === 'new-listings-per-day' ? 'New listings per day' : undefined} key={metric.id} />)}
    </section> : null}
    {inventory ? <FacebookCategoryPanel
      categories={breakdowns['facebook-categories'] ?? []}
      groups={breakdowns['facebook-groups'] ?? []}
      selectedGroupId={groupId ?? null}
      onGroupChange={onGroupChange ?? (() => undefined)}
      from={from}
      toExclusive={toExclusive}
    /> : null}
    {inventory && ((breakdowns['live-by-source']?.length ?? 0) > 0 || (breakdowns['arrival-hours']?.length ?? 0) > 0) ? <section className="chart-grid inventory-charts-row">
      {(breakdowns['live-by-source']?.length ?? 0) > 0 ? <SourceSplitPanel data={breakdowns['live-by-source']} brokerPrivate={breakdowns['broker-private'] ?? []} cityBrokerPrivate={breakdowns['city-broker-private'] ?? []} /> : null}
      {(breakdowns['arrival-hours']?.length ?? 0) > 0 ? <ListingArrivalTimePanel data={breakdowns['arrival-hours']} /> : null}
    </section> : null}
    {averagePerUser ? <>
      <section className="metric-pairs-grid">
        {pairDefinitions.map(([title, averageId, medianId, seriesKey, unit]) => {
          const average = metricById(metrics, averageId);
          const median = metricById(metrics, medianId);
          if (!average || !median) return null;
          return <MetricPairCard key={title} title={title} average={average} median={median} data={series[seriesKey] ?? []} unit={unit} />;
        })}
      </section>
    </> : null}
    {alertsEngagement ? <section className="alerts-metric-rows">
      <div className="alerts-metric-row-group">
        <p className="section-label alerts-metric-row__eyebrow">Alert volume</p>
        <div className="metric-grid alerts-metric-row alerts-metric-row--single">{alertMetrics(['total-sent', 'push-sent', 'email-sent']).map((metric) => <MetricCard metric={metric} note={metric.id === 'push-sent' || metric.id === 'email-sent' ? '' : undefined} key={metric.id} />)}</div>
      </div>
      <div className="alerts-metric-row-group">
        <p className="section-label alerts-metric-row__eyebrow">Engagement rates</p>
        <div className="metric-grid alerts-metric-row alerts-metric-row--four">{alertMetrics(['push-engagement-rate', 'listing-open-rate', 'email-open-rate', 'email-click-rate']).map((metric) => <MetricCard metric={metric} note="" key={metric.id} />)}</div>
      </div>
      <div className="alerts-metric-row-group">
        <p className="section-label alerts-metric-row__eyebrow">Opt-out rates</p>
        <div className="metric-grid alerts-metric-row alerts-metric-row--two">{alertMetrics(['instant-opt-out', 'digest-opt-out']).map((metric) => <MetricCard metric={metric} note="" key={metric.id} />)}</div>
      </div>
    </section> : null}
    {scraperHealth ? <section className="alerts-metric-rows scraper-metric-rows">
      <div className="alerts-metric-row-group">
        <p className="section-label alerts-metric-row__eyebrow">Runs</p>
        <div className="metric-grid alerts-metric-row">{[
          totalRunsMetric,
          ...scraperRunTotals.map((source) => scraperRunMetric(source, `Total ${source.label} runs`)),
        ].map((metric) => <MetricCard metric={metric} key={metric.id} />)}</div>
      </div>
      <div className="alerts-metric-row-group">
        <p className="section-label alerts-metric-row__eyebrow">Failures</p>
        <div className="metric-grid alerts-metric-row">{[
          metricById(metrics, 'failed-runs'), ...scraperFailures,
        ].filter((metric): metric is AnalyticsMetric => metric !== undefined).map((metric) => <MetricCard metric={metric} invertTrend label={metric.id === 'failed-runs' ? 'Total run failures' : undefined} trend={metric.id === 'failed-runs' || metric.id === 'failed-yad2-runs' || metric.id === 'failed-facebook-runs' ? series[metric.id] : undefined} showFailureDetails={metric.id === 'failed-runs' || metric.id === 'failed-yad2-runs' || metric.id === 'failed-facebook-runs'} trendTitle={`${metric.label} per day`} key={metric.id} />)}</div>
      </div>
    </section> : null}
    {globalUsage && (breakdowns['active-residency']?.length ?? 0) > 0 ? <ActiveResidencyCards data={breakdowns['active-residency']} /> : null}
    {retentionMetrics.length > 0 ? <section className="retention-section">
      <p className="section-label retention-section__eyebrow">D1 / D7 / D30 retention — aggregate</p>
      <div className="panel retention-panel">
        <div className="retention-grid">
          {retentionMetrics.map((metric) => <MetricCard metric={metric} label={retentionLabels[metric.id]} note={retentionNotes[metric.id]} key={metric.id} />)}
        </div>
      </div>
    </section> : null}
    {!globalUsage ? <>
    {(preferenceProfiles ? preferenceMetrics : otherMetrics).length > 0 ? <section className={`metric-grid${preferenceProfiles ? ' metric-grid--four' : ''}`}>{(preferenceProfiles ? preferenceMetrics : otherMetrics).map((metric) => <MetricCard metric={metric} label={preferenceProfiles ? preferenceMetricLabels[metric.id] : undefined} note={preferenceProfiles && preferenceHiddenNotes.has(metric.id) ? '' : undefined} key={metric.id} />)}</section> : null}
    {preferenceProfiles ? <>
      <DemandConcentrationPanel cities={breakdowns.cities ?? []} neighborhoods={breakdowns.neighborhoods ?? []} />
      <section className="budget-distribution-section">
        <BudgetDistributionPanel data={breakdowns.budget ?? []} median={metricById(metrics, 'median-budget-max')} />
      </section>
      <PreferenceDemandPanel features={breakdowns.features ?? []} rooms={breakdowns.rooms ?? []} />
    </> : null}
    {alertsEngagement ? <>
      <p className="section-label alerts-charts__eyebrow">Delivery & account settings</p>
      <section className="chart-grid">
        <BreakdownSplitCard title={breakdownTitles['delivery-mix']} data={breakdowns['delivery-mix'] ?? []} />
        <BreakdownSplitCard title={breakdownTitles['profile-overrides']} data={breakdowns['profile-overrides'] ?? []} />
      </section>
    </> : null}
    {!globalUsage && series['active-users'] ? <article className="panel chart-panel chart-panel--wide">
      <div className="panel-title"><h2>Active users over time</h2><span>DAU and trailing MAU</span></div>
      <Suspense fallback={<div className="chart-empty">Loading chart…</div>}>
        <ActivityChart data={series['active-users']} />
      </Suspense>
    </article> : null}
    {Object.entries(series).filter(([key]) => key !== 'active-users' && !(inventory && key === 'new-listings') && !(scraperHealth && (key === 'failed-runs' || key === 'failed-yad2-runs' || key === 'failed-facebook-runs' || key === 'blocking-incidents' || key === 'blocking-yad2' || key === 'blocking-facebook')) && !averagePerUser).map(([key, data]) => {
      const title = seriesTitles[key] ?? key.replaceAll('-', ' ');
      return <article className="panel chart-panel chart-panel--wide" key={key}>
        <div className="panel-title"><h2>{title}</h2><span>Israel calendar days</span></div>
        <Suspense fallback={<div className="chart-empty">Loading chart…</div>}>
          <TimeSeriesChart title={title} data={data} />
        </Suspense>
      </article>;
    })}
    {scraperHealth ? <>
      <p className="section-label scraper-charts__eyebrow">Runtimes</p>
      <RuntimeStatCards average={breakdowns['source-average-durations'] ?? []} maximum={breakdowns['source-maximum-durations'] ?? []} />
    </> : null}
    <section className="chart-grid">
      {Object.entries(breakdowns).filter(([key, data]) => key !== 'facebook-group-options' && key !== 'city-broker-private' && !(scraperHealth && (key === 'facebook-groups' || key === 'sources' || key === 'run-totals' || key === 'source-average-durations' || key === 'source-maximum-durations' || key === 'facebook-group-average-durations' || key === 'facebook-group-maximum-durations')) && !(alertsEngagement && (key === 'delivery-mix' || key === 'profile-overrides')) && !(globalUsage && key === 'active-residency') && !(inventory && (key === 'arrival-hours' || key === 'arrival-weekdays' || key === 'broker-private' || key === 'live-by-source' || key === 'facebook-categories' || key === 'facebook-groups')) && !(preferenceProfiles && (key === 'budget' || key === 'cities' || key === 'neighborhoods' || key === 'features' || key === 'rooms')) && data.length > 0).map(([key, data]) => (
        <BreakdownPanel key={key} title={breakdownTitles[key] ?? key.replaceAll('-', ' ')} data={data} />
      ))}
    </section>
    {scraperHealth ? <ScraperConfigEditor configs={scraperConfigs} error={scraperConfigError} /> : null}
    </> : null}
  </>;
}

export function DashboardPage({ section }: { section: SectionDefinition }) {
  const [params, setParams] = useSearchParams();
  const requestedPreset = (params.get('range') as RangePreset | null) ?? '30d';
  const preset = requestedPreset;
  const defaultRange = rangeForPreset(preset === 'custom' ? '30d' : preset);
  const from = params.get('from') ?? defaultRange.from;
  const toExclusive = params.get('to') ?? defaultRange.toExclusive;
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(() => section.groupFilter && params.get('groupId') ? Number(params.get('groupId')) : null);
  const refreshRef = useRef(false);
  const report = useQuery({
    queryKey: ['analytics-report', section.id, from, toExclusive],
    queryFn: async ({ signal }) => {
      const refresh = refreshRef.current;
      refreshRef.current = false;
      return getAnalyticsReport({ section: section.id, from, toExclusive, groupId: null, refresh, signal });
    },
  });
  const scraperConfigs = useQuery({
    queryKey: ['scraper-configurations'],
    queryFn: () => getScraperConfigurations(),
    enabled: section.id === 'scraper-health',
  });

  function update(next: Record<string, string | null>) {
    setParams((current) => {
      for (const [key, value] of Object.entries(next)) value === null ? current.delete(key) : current.set(key, value);
      return current;
    }, { replace: true });
  }

  return <div className="page">
    <header className="page-heading">
      <div>
        {/* <p className="eyebrow">{section.id === 'overview' ? 'Lifetime · Day 0 → today' : 'Reacts to date range selection'}</p> */}
        <h1>{section.title}</h1><p>{section.description}</p></div>
      {section.id === 'overview' ? <span className="range-chip">Day 0 → today</span> : <div className="page-heading__tools">
        <DateRangeMenu
          preset={preset}
          from={from}
          toExclusive={toExclusive}
          allowAllTime
          onPreset={(value) => {
            const range = rangeForPreset(value);
            update({ range: value, from: range.from, to: range.toExclusive });
          }}
          onCustomRange={(customFrom, customToExclusive) => update({
            range: 'custom', from: customFrom, to: customToExclusive,
          })}
        />
        <button
          className="icon-button"
          type="button"
          onClick={() => { refreshRef.current = true; void report.refetch(); }}
          disabled={report.isFetching}
          aria-label="Refresh analytics"
        ><Icon name="refresh" size={17} /></button>
      </div>}
    </header>
    {report.isLoading ? <LoadingState /> : report.error ? <ErrorState message={report.error.message} onRetry={() => void report.refetch()} /> : report.data ? (
      section.id === 'overview'
        ? <Overview metrics={report.data.data.metrics} series={report.data.data.series} breakdowns={report.data.data.breakdowns} />
        : <StandardDashboard averagePerUser={section.id === 'average-per-user'} globalUsage={section.id === 'global-usage'} preferenceProfiles={section.id === 'preference-profiles'} inventory={section.id === 'listing-inventory'} alertsEngagement={section.id === 'alerts-engagement'} scraperHealth={section.id === 'scraper-health'} scraperConfigs={scraperConfigs.data} scraperConfigError={scraperConfigs.error?.message} groupId={selectedGroupId} onGroupChange={setSelectedGroupId} from={from} toExclusive={toExclusive} metrics={report.data.data.metrics} series={report.data.data.series} breakdowns={report.data.data.breakdowns} />
    ) : null}
    {report.data ? <p className="generated-at">Generated {new Date(report.data.generatedAt).toLocaleString('en-GB')} · cached for five minutes</p> : null}
  </div>;
}
